import React, { useState } from 'react';
import { Image as ImageIcon, Send, Heart, MessageCircle, Share2, MoreHorizontal } from 'lucide-react';
import { feedPosts } from '../../data/mockData';
interface FeedPageProps {
  tenantId: string;
  onNavigate: (page: string, id?: string) => void;
}
export function FeedPage({
  tenantId,
  onNavigate
}: FeedPageProps) {
  const [activeTab, setActiveTab] = useState('all');
  const [postContent, setPostContent] = useState('');
  const tenantPosts = feedPosts.filter(p => p.tenantId === tenantId);
  const filteredPosts = tenantPosts.filter(p => {
    if (activeTab === 'news') return p.type === 'news';
    if (activeTab === 'sponsored') return p.isSponsored;
    if (activeTab === 'posts') return p.type === 'post';
    return true;
  });
  return <div className="max-w-3xl mx-auto space-y-6" data-id="element-441">
      {/* Composer */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-id="element-442">
        <div className="flex gap-4" data-id="element-443">
          <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold flex-shrink-0" data-id="element-444">
            AD
          </div>
          <div className="flex-1" data-id="element-445">
            <textarea placeholder="Teilen Sie etwas mit dem Netzwerk..." value={postContent} onChange={e => setPostContent(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none resize-none min-h-[100px]" data-id="element-446" />
            <div className="flex items-center justify-between mt-3" data-id="element-447">
              <button className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" data-id="element-448">
                <ImageIcon size={20} data-id="element-449" />
              </button>
              <button className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${postContent.trim() ? 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`} data-id="element-450">
                <Send size={16} data-id="element-451" /> Posten
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Feed Filters */}
      <div className="flex overflow-x-auto hide-scrollbar gap-2 border-b border-slate-200 pb-2" data-id="element-452">
        {[{
        id: 'all',
        label: 'Alle Beiträge'
      }, {
        id: 'posts',
        label: 'Mitglieder'
      }, {
        id: 'news',
        label: 'Club News'
      }, {
        id: 'sponsored',
        label: 'Partner Angebote'
      }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${activeTab === tab.id ? 'bg-slate-800 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-453">
            {tab.label}
          </button>)}
      </div>

      {/* Feed Stream */}
      <div className="space-y-6" data-id="element-454">
        {filteredPosts.map(post => <div key={post.id} className={`bg-white rounded-xl border shadow-sm overflow-hidden ${post.isSponsored ? 'border-blue-200' : post.type === 'news' ? 'border-emerald-200' : 'border-slate-200'}`} data-id="element-455">
            {/* Post Header */}
            <div className="p-4 flex items-start justify-between" data-id="element-456">
              <div className="flex items-center gap-3" data-id="element-457">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold cursor-pointer ${post.type === 'news' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`} onClick={() => post.authorId !== 'admin' && onNavigate('person-profile', post.authorId)} data-id="element-458">
                  {post.authorAvatar}
                </div>
                <div data-id="element-459">
                  <div className="flex items-center gap-2" data-id="element-460">
                    <h3 className="font-bold text-slate-900 cursor-pointer hover:text-emerald-600 transition-colors" onClick={() => post.authorId !== 'admin' && onNavigate('person-profile', post.authorId)} data-id="element-461">
                      {post.authorName}
                    </h3>
                    {post.type === 'news' && <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wider rounded-full" data-id="element-462">
                        Offiziell
                      </span>}
                  </div>
                  <p className="text-xs text-slate-500" data-id="element-463">
                    {post.authorRole} • {post.authorCompany}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5" data-id="element-464">
                    {post.createdAt}
                  </p>
                </div>
              </div>
              <button className="text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-100 transition-colors" data-id="element-465">
                <MoreHorizontal size={20} data-id="element-466" />
              </button>
            </div>

            {/* Post Content */}
            <div className="px-4 pb-3" data-id="element-467">
              <p className="text-slate-800 whitespace-pre-wrap" data-id="element-468">
                {post.content}
              </p>
              {post.imageEmoji && <div className="mt-4 w-full h-48 bg-slate-50 rounded-lg border border-slate-100 flex items-center justify-center text-6xl" data-id="element-469">
                  {post.imageEmoji}
                </div>}
            </div>

            {/* Sponsored Banner */}
            {post.isSponsored && <div className="px-4 py-2 bg-blue-50 border-t border-b border-blue-100 flex justify-between items-center" data-id="element-470">
                <span className="text-xs font-bold text-blue-700 uppercase tracking-wider" data-id="element-471">
                  Gesponsert
                </span>
                <button className="text-sm font-medium text-blue-700 hover:text-blue-800" data-id="element-472">
                  Mehr erfahren &rarr;
                </button>
              </div>}

            {/* Interactions */}
            <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between" data-id="element-473">
              <div className="flex items-center gap-6" data-id="element-474">
                <button className="flex items-center gap-2 text-slate-500 hover:text-red-500 transition-colors group" data-id="element-475">
                  <Heart size={18} className="group-hover:fill-red-500" data-id="element-476" />
                  <span className="text-sm font-medium" data-id="element-477">{post.likes}</span>
                </button>
                <button className="flex items-center gap-2 text-slate-500 hover:text-emerald-600 transition-colors" data-id="element-478">
                  <MessageCircle size={18} data-id="element-479" />
                  <span className="text-sm font-medium" data-id="element-480">{post.comments}</span>
                </button>
              </div>
              <button className="flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors" data-id="element-481">
                <Share2 size={18} data-id="element-482" />
                <span className="text-sm font-medium hidden sm:inline" data-id="element-483">
                  Teilen
                </span>
              </button>
            </div>
          </div>)}
      </div>
    </div>;
}